import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    LocalShipping as ShippingIcon,
    Inventory as InventoryIcon,
    CalendarToday as CalendarIcon,
    History as HistoryIcon,
    SyncAlt as SyncAltIcon,
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Schedule as ScheduleIcon,
    Notes as NotesIcon,
    Receipt as ReceiptIcon,
    Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { format } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../utils/request';
import { ProductReturn, Status, StatusActivity, StatusNoteCreate, Item } from '../../types';
import { toast } from 'react-toastify';
import { getStatusColor, getStatusIconBall } from '../ListProductReturn';

// Status configuration
const STATUS_OPTIONS: Status[] = [
    'RETURN_CREATED',
    'RECEIVED_AT_SHOP',
    'SENT_TO_VENDOR',
    'RECEIVED_BY_VENDOR'
];

const STATUS_CONFIG: Record<Status, { label: string; color: string; icon: string }> = {
    RETURN_CREATED: { label: 'Return Created', color: 'info', icon: '🟢' },
    RECEIVED_AT_SHOP: { label: 'Received at Shop', color: 'primary', icon: '🟡' },
    SENT_TO_VENDOR: { label: 'Sent to Vendor', color: 'warning', icon: '🟣' },
    RECEIVED_BY_VENDOR: { label: 'Received by Vendor', color: 'info', icon: '⚫' }
};

const getStatusIcon = (status?: string) => {
    switch (status) {
        case 'RETURN_CREATED': return <ReceiptIcon fontSize="small" />;
        case 'AWAITING_RECEIPT': return <ScheduleIcon fontSize="small" />;
        case 'RECEIVED_AT_SHOP': return <WarehouseIcon fontSize="small" />;
        case 'INSPECTED': return <AssignmentIcon fontSize="small" />;
        case 'SENT_TO_VENDOR': return <ShippingIcon fontSize="small" />;
        case 'RECEIVED_BY_VENDOR': return <BusinessIcon fontSize="small" />;
        case 'RESOLVED': return <CheckCircleIcon fontSize="small" />;
        case 'CLOSED': return <CheckCircleIcon fontSize="small" />;
        default: return <ReceiptIcon fontSize="small" />;
    }
};

const ShowProductReturn = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [productReturn, setProductReturn] = useState<ProductReturn | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [party, setParty] = useState<any | null>(null);
    const [vendor, setVendor] = useState<any | null>(null);
    const [items, setItems] = useState<Item[]>([]);
    const [statusHistory, setStatusHistory] = useState<StatusActivity[]>([]);
    const [statusNotes, setStatusNotes] = useState<StatusNoteCreate[]>([]);

    // Status change dialog
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<string>('');
    const [statusNote, setStatusNote] = useState<string>('');
    const [changingStatus, setChangingStatus] = useState(false);

    const fetchProductReturn = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await request({
                url: `productReturn/get-single-product-return/${id}`,
                method: 'get',
            });

            if (response.success && response.data) {
                const returnData = response.data;
                setProductReturn(returnData);
                setParty(returnData.party || null);
                setVendor(returnData.vendor || null);
                setItems(returnData.items || []);

                // Make sure these are set correctly
                setStatusHistory(returnData.status_history || []);
                setStatusNotes(returnData.status_notes || []);

                // Set initial status history if empty (for backward compatibility)
                if ((!returnData.status_history || returnData.status_history.length === 0) && returnData.status) {
                    const initialHistory: StatusActivity = {
                        from_status: undefined,
                        to_status: returnData.status,
                        note: returnData.status_notes?.[0]?.note || '',
                        changed_at: returnData.createdAt || new Date(),
                        is_initial: true,
                    };
                    setStatusHistory([initialHistory]);
                }
            } else {
                setError('Product return not found');
            }
        } catch (err: any) {
            console.error('Error fetching product return:', err);
            setError(err.message || 'Failed to load product return details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProductReturn();
    }, [fetchProductReturn]);

    const handleStatusChange = async () => {
        if (!newStatus || !productReturn || newStatus === productReturn.status) return;

        setChangingStatus(true);
        try {
            const payload: any = {
                status: newStatus,
                status_note: statusNote.trim()
            };

            const res = await request({
                url: `productReturn/update-product-return/${id}`,
                method: 'put',
                data: payload,
            });

            if (res.success) {
                toast.success('Status updated successfully!');
                setStatusDialogOpen(false);
                setNewStatus('');
                setStatusNote('');

                await fetchProductReturn(); // Refresh the data
            } else {
                toast.error(res.message || 'Failed to update status');
            }
        } catch (err: any) {
            console.error('Status change error:', err);
            toast.error(err.message || 'Failed to update status');
        } finally {
            setChangingStatus(false);
        }
    };

    const openStatusDialog = () => {
        if (productReturn) {
            setNewStatus(productReturn.status ?? '');
            setStatusNote('');
            setStatusDialogOpen(true);
        }
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        try {
            return format(new Date(date), 'dd MMM yyyy, HH:mm');
        } catch {
            return 'Invalid date';
        }
    };

    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        try {
            return format(new Date(date), 'dd/MM/yyyy');
        } catch {
            return 'Invalid date';
        }
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (item.amount || 0), 0);
    };

    const calculateTotalQuantity = () => {
        return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    };

    // Get activity timeline
    const getActivityTimeline = () => {
        const timeline = [...statusHistory];

        // Sort by date (newest first)
        timeline.sort((a, b) => {
            const dateA = new Date(a.changed_at || 0);
            const dateB = new Date(b.changed_at || 0);
            return dateB.getTime() - dateA.getTime();
        });

        return timeline;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading product return details...
                </Typography>
            </Box>
        );
    }

    if (error || !productReturn) {
        return (
            <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                    <Brandcrumb />
                </Box>
                <Card>
                    <CardContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error || 'Product return not found'}
                        </Alert>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/productReturn')}
                            size="small"
                        >
                            Back to Product Returns
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const activityTimeline = getActivityTimeline();

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
                <Chip
                    onClick={() => navigate(-1)}
                    icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
                    label="Back"
                    variant="filled"
                    color="secondary"
                    sx={{
                        fontSize: '14px',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate('/productReturn')}
                            size="small"
                            sx={{ color: 'text.primary' }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Product Return
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {productReturn.productReturnNumber}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Product Return" placement='top' arrow>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/productReturn/editProductReturn/${id}`)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2 }}>

                    {/* LEFT COLUMN - Return Information */}
                    <Box sx={{ flex: 1 }}>
                        {/* Top Row: Return Date on Left, Status on Right */}
                        <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                            {/* Return Date Card */}
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                py: 1
                            }}>
                                <CalendarIcon fontSize="small" color="action" sx={{ mr: 1.5 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Return Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(productReturn.productReturnDate)}
                                    </Typography>
                                </Box>
                            </Card>

                            {/* Status Card with Change Button */}
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {getStatusIcon(productReturn.status)}
                                    <Box sx={{ ml: 1.5 }}>
                                        <Stack direction={'row'} spacing={6}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Current Status
                                            </Typography>
                                        </Stack>

                                        <Stack direction={'row'} spacing={.5} alignItems={"center"}>
                                            <Chip
                                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <span>{getStatusIconBall(productReturn.status)}</span>
                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                        {STATUS_CONFIG[productReturn.status as Status]?.label || productReturn.status}
                                                    </Typography>
                                                </Box>}
                                                color={getStatusColor(productReturn.status) as any}
                                                size="small"
                                                sx={{ height: 22, fontSize: '0.75rem', mt: 0.5 }}
                                            />
                                            <Tooltip title="Update Status" arrow placement='top'>
                                                <IconButton size="small" color="primary" onClick={openStatusDialog}>
                                                    <SyncAltIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Card>
                        </Box>

                        {/* Second Row: In Date on Left, Out Date on Right */}
                        <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                            {/* In Date Card */}
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                py: 1
                            }}>
                                <WarehouseIcon fontSize="small" color="action" sx={{ mr: 1.5 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        In Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(productReturn.in_date) || 'Not set'}
                                    </Typography>
                                </Box>
                            </Card>

                            {/* Out Date Card */}
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                py: 1
                            }}>
                                <ShippingIcon fontSize="small" color="action" sx={{ mr: 1.5 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Out Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(productReturn.out_date) || 'Not set'}
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>

                        {/* Summary Cards */}
                        <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                py: 1,
                                bgcolor: alpha('#1976d2', 0.05)
                            }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Total Items
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary.main">
                                    {items.length}
                                </Typography>
                            </Card>

                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                py: 1,
                                bgcolor: alpha('#2e7d32', 0.05)
                            }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Total Quantity
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                    {calculateTotalQuantity()}
                                </Typography>
                            </Card>

                            <Card elevation={0} sx={{
                                flex: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2,
                                py: 1,
                                bgcolor: alpha('#ed6c02', 0.05)
                            }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Total Amount
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="warning.main">
                                    ₹{calculateTotal().toFixed(2)}
                                </Typography>
                            </Card>
                        </Box>

                        {/* Description Section */}
                        {productReturn.description && (
                            <Card elevation={0} sx={{
                                borderRadius: 1,
                                mb: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 2
                            }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotesIcon fontSize="small" />
                                    Description
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                                    {productReturn.description}
                                </Typography>
                            </Card>
                        )}

                        {/* Items Table */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InventoryIcon fontSize="small" />
                                Return Items
                            </Typography>

                            {items.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.default' }}>
                                            <TableRow>
                                                <TableCell>Sr No</TableCell>
                                                <TableCell>Product</TableCell>
                                                <TableCell>HSN</TableCell>
                                                <TableCell align="right">Quantity</TableCell>
                                                <TableCell align="right">Price (₹)</TableCell>
                                                <TableCell align="right">Amount (₹)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{item.srNo || index + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{item.itemName}</Typography>
                                                        {item.aliasName && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {item.aliasName}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{item.hsnNo || '-'}</TableCell>
                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                    <TableCell align="right">₹{item.price?.toFixed(2)}</TableCell>
                                                    <TableCell align="right">₹{item.amount?.toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: 'background.default' }}>
                                                <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                                                    Total:
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    ₹{calculateTotal().toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No items in this return
                                </Typography>
                            )}
                        </Card>

                        {/* Activity Timeline */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon fontSize="small" />
                                Activity Timeline
                            </Typography>

                            {activityTimeline.length > 0 ? (
                                <List sx={{
                                    position: 'relative',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        left: 12,
                                        top: 5,
                                        bottom: 0,
                                        width: 2,
                                        backgroundColor: 'divider'
                                    },
                                    py: 0
                                }}>
                                    {activityTimeline.map((activity, index) => {
                                        // Find matching status note for this activity
                                        const matchingNote = statusNotes.find(note =>
                                            note.current_status === activity.to_status &&
                                            (!activity.from_status || note.previous_status === activity.from_status)
                                        )

                                        return (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    position: 'relative',
                                                    alignItems: 'flex-start',
                                                    py: 1.5,
                                                    px: 0,
                                                    '&:last-child': {
                                                        pb: 0
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar sx={{ minWidth: 36 }}>
                                                    <Avatar sx={{
                                                        width: 24,
                                                        height: 24,
                                                        bgcolor: getStatusColor(activity.to_status) + '.main',
                                                        color: 'white',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {getStatusIcon(activity.to_status)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    sx={{ m: 0 }}
                                                    primary={
                                                        <Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                                <Box>
                                                                    {activity.from_status ? (
                                                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                                            <span>{getStatusIconBall(activity.from_status)}</span>
                                                                            <span style={{ fontWeight: 'bold' }}>{STATUS_CONFIG[activity.from_status as Status]?.label || activity.from_status}</span>
                                                                            <span>→</span>
                                                                            <span>{getStatusIconBall(activity.to_status)}</span>
                                                                            <span style={{ fontWeight: 'bold' }}>{STATUS_CONFIG[activity.to_status as Status]?.label || activity.to_status}</span>
                                                                        </Typography>
                                                                    ) : (
                                                                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                                            <span>{getStatusIconBall(activity.to_status)}</span>
                                                                            <span>Return created as </span>
                                                                            <span style={{ fontWeight: 'bold' }}>{STATUS_CONFIG[activity.to_status as Status]?.label || activity.to_status}</span>
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', ml: 1 }}>
                                                                    {formatDateTime(activity.changed_at)}
                                                                </Typography>
                                                            </Box>

                                                            {/* Show note from activity or matching status note */}
                                                            {(activity.note || matchingNote?.note) && (
                                                                <Paper elevation={0} sx={{
                                                                    py: 0.5,
                                                                    px: 1,
                                                                    mt: 1,
                                                                    bgcolor: 'background.default',
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    borderRadius: 0.5
                                                                }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                                        {activity.note || matchingNote?.note}
                                                                    </Typography>
                                                                </Paper>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No activity history available
                                </Typography>
                            )}
                        </Card>
                    </Box>

                    {/* RIGHT COLUMN - Party & Vendor Information */}
                    <Box sx={{ flex: 1 }}>
                        {/* Party Information */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" />
                                From Party
                            </Typography>

                            {party ? (
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Party Name
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {party.partyName || party.name || 'Unknown'}
                                            {party.nickName && (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({party.nickName})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>

                                    {party.contact?.phone && party.contact.phone.length > 0 && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                Contact
                                            </Typography>
                                            <Stack spacing={1}>
                                                {party.contact.phone.slice(0, 2).map((phone: any, index: number) => (
                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PersonIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                            {phone.phoneNo || phone.phone_no}
                                                            {phone.label && (
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    ({phone.label})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {party.contact.phone.length > 2 && (
                                                    <Tooltip title={
                                                        <Box>
                                                            {party.contact.phone.slice(2).map((phone: any, idx: number) => (
                                                                <div key={idx}>{phone.phoneNo || phone.phone_no}</div>
                                                            ))}
                                                        </Box>
                                                    } arrow>
                                                        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer', ml: 4 }}>
                                                            +{party.contact.phone.length - 2} more
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No party information available
                                </Typography>
                            )}
                        </Card>

                        {/* Vendor Information */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon fontSize="small" />
                                To Vendor
                            </Typography>

                            {vendor ? (
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Vendor Name
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {vendor.vendorName || vendor.name || 'Unknown'}
                                        </Typography>
                                    </Box>

                                    {vendor.contact?.phone && vendor.contact.phone.length > 0 && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                Contact
                                            </Typography>
                                            <Stack spacing={1}>
                                                {vendor.contact.phone.slice(0, 2).map((phone: any, index: number) => (
                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BusinessIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                            {phone.phoneNo || phone.phone_no}
                                                            {phone.label && (
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    ({phone.label})
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {vendor.contact.phone.length > 2 && (
                                                    <Tooltip title={
                                                        <Box>
                                                            {vendor.contact.phone.slice(2).map((phone: any, idx: number) => (
                                                                <div key={idx}>{phone.phoneNo || phone.phone_no}</div>
                                                            ))}
                                                        </Box>
                                                    } arrow>
                                                        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer', ml: 4 }}>
                                                            +{vendor.contact.phone.length - 2} more
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No vendor information available
                                </Typography>
                            )}
                        </Card>

                        {/* Metadata */}
                        <Card elevation={0} sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon fontSize="small" />
                                Metadata
                            </Typography>

                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Return Number
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {productReturn.productReturnNumber}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Created Date
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(productReturn.productReturnDate)}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatDate(productReturn.updatedAt || productReturn.created_at)}
                                    </Typography>
                                </Box>

                                {productReturn.created_at && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            System Created
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {formatDate(productReturn.created_at)}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Card>
                    </Box>
                </Box>
            </Box>

            {/* Status Change Dialog */}
            <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <SyncAltIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                        Update Status
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Current Status
                            </Typography>
                            <Chip
                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{getStatusIconBall(productReturn?.status)}</span>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {productReturn?.status && STATUS_CONFIG[productReturn.status as Status]?.label}
                                    </Typography>
                                </Box>}
                                color={getStatusColor(productReturn?.status) as any}
                                size="small"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        </Box>

                        <FormControl fullWidth size="small">
                            <InputLabel>New Status *</InputLabel>
                            <Select
                                value={newStatus}
                                label="New Status *"
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                {STATUS_OPTIONS.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{STATUS_CONFIG[status].icon}</span>
                                            {STATUS_CONFIG[status].label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label="Status Note *"
                            placeholder="Explain why you're changing the status..."
                            value={statusNote}
                            onChange={(e) => setStatusNote(e.target.value)}
                            helperText="This note will be shown in the activity timeline"
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setStatusDialogOpen(false)}
                        disabled={changingStatus}
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStatusChange}
                        disabled={!newStatus || !statusNote.trim() || changingStatus || newStatus === productReturn?.status}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                    >
                        {changingStatus ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ShowProductReturn;